import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/ConfirmationModal';
import { useMessage } from 'components/ui';
import {
  type AtlasGqlDamageInspectTask,
  type AtlasGqlDamageRepairTask,
  AtlasGqlTaskTargetType,
  useGetTaskTargetsQuery,
  useUpdateTaskTargetsMutation,
} from 'types/atlas-graphql';

import { getPrimaryObservationGroupForDamage } from '../utils';

type UpdateToPrimaryModalProps = {
  taskIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export const UpdateToPrimaryModal: React.FunctionComponent<UpdateToPrimaryModalProps> = ({
  taskIds,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputs, setInputs] = useState<{ taskId: string; inspectionId: string }[]>([]);

  // query for tasks (to get fresh data)
  const { data: taskTargetsData, loading: tasksLoading } = useGetTaskTargetsQuery({
    variables: { filterBy: { key: 'id', values: taskIds }, limit: taskIds.length },
    fetchPolicy: 'network-only',
    skip: !isOpen || taskIds.length === 0,
  });

  useEffect(() => {
    if (!taskTargetsData) {
      return;
    }
    const tasks = taskTargetsData?.tasks?.items;
    if (tasks) {
      const _inputs = tasks
        .filter((task): task is AtlasGqlDamageInspectTask | AtlasGqlDamageRepairTask => !!task)
        .map(task => {
          const taskId = task.id;
          const inspectionId = getPrimaryObservationGroupForDamage(
            task.horizonDamage
          )?.inspectionId;

          return { taskId, inspectionId };
        })
        .filter(input => !!input.taskId && !!input.inspectionId);

      setInputs(_inputs);
    }
  }, [taskTargetsData]);

  // update task mutation
  const [updateTask] = useUpdateTaskTargetsMutation();

  // bulk submit the update task mutation
  const handleSubmit = async () => {
    setIsLoading(true);
    await Promise.all(
      inputs.map(input => {
        const { taskId, inspectionId } = input;
        return updateTask({
          variables: {
            id: taskId,
            input: {
              targets: [
                {
                  targetId: inspectionId,
                  targetType: AtlasGqlTaskTargetType.Inspection,
                },
              ],
            },
          },
        });
      })
    );
    setIsLoading(false);

    message.success(
      t('damage.tasksUpdatedSuccess', {
        count: inputs.length,
        defaultValue:
          inputs.length > 1
            ? '{{count}} Tasks were successfully updated.'
            : 'Task was successfully updated.',
      })
    );

    onSuccess && onSuccess();
    onClose();
  };

  return (
    <ConfirmationModal
      open={isOpen}
      loading={tasksLoading || isLoading}
      title={t('damage.confirmUpdate', 'Confirm Update')}
      okText={t('common.update', 'Update')}
      onOk={handleSubmit}
      cancelText={t('common.cancel', 'Cancel')}
      onCancel={onClose}
      message={t('damage.updateToPrimaryWarning', {
        count: inputs.length,
        defaultValue:
          inputs.length > 1
            ? "This action will update the Task Targets for these {{count}} Tasks to the Damage's Primary Observation Group. This will affect the Damage attributes and photos associated with these {{count}} Tasks. Do you wish to continue?"
            : "This action will update the Task Target for this Task to the Damage's Primary Observation Group. This will affect the Damage attributes and photos associated with this Task. Do you wish to continue?",
      })}
      destroyOnHidden
    />
  );
};
